import React, { useState, useEffect, useContext } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import IconCancel from "@material-ui/icons/Cancel";
import IconSave from "@material-ui/icons/Save";
import _ from "lodash";
import PlayBaseFields from "./PlayBaseFields";
import Button from "@material-ui/core/Button";
import { makeStyles } from "@material-ui/core/styles";
import {
  fieldExistsOnPlayType,
  requiredFields,
  getPlayTypeSuggestions,
  convertNumByFieldName,
  getPreviousPlay,
  getPlayTypeNameById
} from "games/utils";
import { isDisplayable } from "util/misc";
import { allPlays } from "games/allPlays";
import { PlayByPlayContext } from "games/model/PlayByPlayContext";
import { midPlayChanges } from "games/model/midPlay";
import { prePlayChanges } from "games/model/prePlay";
import { getPlayFieldsByPlayTypeId } from "games/utils";
import WarningButton from "components/WarningButton";

const useStyles = makeStyles({
  dialogContent: {
    display: "flex",
    flexWrap: "wrap"
  }
});

const Play = ({
  playId,
  showDialog,
  handleCloseClick,
  actionOnSubmit,
  dialogTitle
}) => {
  const {
    usePlayByPlay: [state, dispatch]
  } = useContext(PlayByPlayContext);
  const { game, plays, isLoading } = state;
  const [isInvalid, setIsInvalid] = useState(true);
  const [activePlay, setActivePlay] = useState({});
  const [playTypeSuggestions, setPlayTypeSuggestions] = useState(allPlays);
  const getActivePlayIndex = () => {
    const index = playId ? plays.findIndex((play) => play.id === playId) : null;
    return index !== -1 ? index : null;
  };
  const classes = useStyles();
  const activePlayIndex = getActivePlayIndex();
  const previousPlay = getPreviousPlay(plays, activePlayIndex);
  const activePlayTypeName = getPlayTypeNameById(activePlay.playType);
  const title = activePlayTypeName
    ? `${dialogTitle} - ${activePlayTypeName}`
    : dialogTitle;

  // set current play when opening play from list
  // if new play, playId will not exist
  useEffect(() => {
    if (!showDialog) return;
    const play = playId ? _.get(plays, activePlayIndex, {}) || {} : {};
    setPlayTypeSuggestions(getPlayTypeSuggestions(previousPlay));
    setActivePlay(play);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDialog]);

  // check validity
  // @todo handle no ballspotteamId (@50)
  // @todo this is not the best solution, but works for now...
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let playRequiredFields = requiredFields.filter((field) =>
      fieldExistsOnPlayType(activePlay.playType, field)
    );
    playRequiredFields.push("playType");
    const notAllRequiredFieldsFilled = playRequiredFields.some((field) => {
      return !isDisplayable(_.get(activePlay, field));
    });
    setIsInvalid(notAllRequiredFieldsFilled);
  });

  const handleSubmit = () => {
    dispatch({
      type: actionOnSubmit,
      payload: activePlay
    });
    handleCloseClick();
  };

  const handleInputChange = (value, name) => {
    if (!name) return;
    const transformedPlay = {
      ...activePlay,
      [name]: convertNumByFieldName(value, name)
    };

    const inputIsPlayType = name === "playType";
    // if active play has a play type and the input changed was the playType field
    // that means that the play type was switched, versus a play type getting set on a new play
    const playTypeIsSwitched = Boolean(activePlay.playType) && inputIsPlayType;
    let getPrePlayChanges = {};
    if (playTypeIsSwitched) {
      const previousStatePlayFields = getPlayFieldsByPlayTypeId(
        activePlay.playType
      );
      const activeStatePlayFields = getPlayFieldsByPlayTypeId(value);
      const fieldDiff = previousStatePlayFields.filter(
        (field) => !activeStatePlayFields.includes(field)
      );
      getPrePlayChanges = prePlayChanges({
        previousPlay,
        activePlay: transformedPlay,
        game,
        clearedFields: fieldDiff
      });
    } else if (inputIsPlayType) {
      getPrePlayChanges = prePlayChanges({
        previousPlay,
        activePlay: transformedPlay,
        game
      });
    }

    const getMidPlayChanges = midPlayChanges(name, value, transformedPlay);

    setActivePlay({
      ...transformedPlay,
      ...getPrePlayChanges,
      ...getMidPlayChanges
    });
  };

  const handleDropdownChange = (event, newValue, name) => {
    const value = _.get(newValue, "id", null);
    handleInputChange(value, name);
  };

  const handleDefaultInputChange = (event, name) => {
    const value = event.target.value
      ? event.target.value
      : event.target.checked;
    handleInputChange(value, name);
  };

  const handleButtonClick = (value, name) => {
    handleInputChange(value, name);
  };

  const handleDeletePlayClick = () => {
    const confirmText = `Are you sure you want to delete this Play - ${activePlayTypeName}?`;
    if (window.confirm(confirmText)) {
      handleCloseClick();
      dispatch({
        type: "deletePlay",
        payload: activePlay
      });
    }
  };

  return (
    <Dialog
      open={showDialog}
      onClose={handleCloseClick}
      aria-label={title}
      maxWidth="lg"
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <PlayBaseFields
          handleButtonClick={handleButtonClick}
          handleInputChange={handleDefaultInputChange}
          handleDropdownChange={handleDropdownChange}
          play={activePlay}
          playTypeSuggestions={playTypeSuggestions}
        />
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading || isInvalid}
          color="primary"
        >
          <IconSave /> Save
        </Button>
        <Button color="primary" onClick={handleCloseClick} disabled={isLoading}>
          <IconCancel /> Cancel
        </Button>
        {activePlay.playType && (
          <WarningButton onClick={handleDeletePlayClick} label="Delete" />
        )}
      </DialogActions>
    </Dialog>
  );
};

Play.defaultProps = {
  playId: null
};

export default Play;
